import { useEffect, useState } from "react";
import { Button, Card, EmptyState } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { SupplierDTO, SupplierWithCountDTO } from "@wmart/shared-types";
import { SupplierEditDrawer } from "../components/SupplierEditDrawer.js";
import { SupplierUsersDrawer } from "../components/SupplierUsersDrawer.js";

export function SuppliersList() {
  const { notify, notifyProblem } = useToast();
  const [suppliers, setSuppliers] = useState<SupplierWithCountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SupplierDTO | null>(null);
  const [creating, setCreating] = useState(false);
  const [usersFor, setUsersFor] = useState<SupplierDTO | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<{ suppliers: SupplierWithCountDTO[] }>(
        "/v1/admin/suppliers",
      );
      setSuppliers(res.suppliers);
    } catch {
      notify("error", "Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(s: SupplierWithCountDTO) {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try {
      await api.delete(`/v1/admin/suppliers/${s.id}`);
      notify("success", "Supplier deleted");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Delete failed");
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Suppliers</h1>
        <Button onClick={() => setCreating(true)}>New supplier</Button>
      </div>
      {loading ? (
        <p className="text-body text-ink-500">Loading…</p>
      ) : suppliers.length === 0 ? (
        <EmptyState
          icon="package"
          title="No suppliers yet"
          body="Register the companies WMart buys from, then assign products to them."
        />
      ) : (
        <Card className="divide-y divide-ink-100">
          {suppliers.map((s) => (
            <div key={s.id} className="flex items-center gap-4 px-4 py-3">
              <div className="flex-1">
                <p className="text-body font-medium text-ink-900">
                  {s.name}
                  {!s.active && (
                    <span className="ml-2 rounded-sm bg-ink-100 px-1.5 py-0.5 text-caption text-ink-500">
                      inactive
                    </span>
                  )}
                </p>
                <p className="font-mono text-caption text-ink-500">
                  {s.slug} · {s.email}
                </p>
              </div>
              <span className="text-body-sm text-ink-500">
                {s.product_count} products
              </span>
              <span className="text-body-sm text-ink-500">
                {s.open_po_count} open POs
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(s)}>
                  Edit
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setUsersFor(s)}>
                  Users
                </Button>
                <Button size="sm" variant="secondary" onClick={() => void remove(s)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
      {creating ? (
        <SupplierEditDrawer
          supplier={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : null}
      {editing ? (
        <SupplierEditDrawer
          supplier={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
      {usersFor ? (
        <SupplierUsersDrawer
          supplier={usersFor}
          onClose={() => setUsersFor(null)}
        />
      ) : null}
    </div>
  );
}
